import { api } from './api';
import { useAuthStore } from '@/stores/authStore';
import type {
  SearchResult,
  SearchOptions,
  ReverseGeocodeResult,
  Coordinates,
  Place,
  PlaceCategory,
  Address,
} from '@/types';

// Full-search and autocomplete run concurrently (the box autocompletes while a
// submitted search is in flight), so they need DISTINCT abort keys — sharing one
// made each new request cancel the other.
const SEARCH_ABORT_KEY = 'search-full';
const AUTOCOMPLETE_ABORT_KEY = 'search-autocomplete';

// ---------------------------------------------------------------------------
// Ranking / personalization engine (search-service /ranking/*).
//
// The search-service ships a built ranking engine that the web app never used.
// The helpers below wire it in: trending-near-you, personalized re-ranking, and
// implicit click/dwell feedback. All of them fail SILENTLY — ranking is an
// enhancement layer, so any error must leave the user's search untouched.
// ---------------------------------------------------------------------------

// Ranking structs have no JSON tags, so Go marshals them with their exact Go
// field names (ID, Name, Lat, Lng, ...). These interfaces mirror that casing.
interface RankingPlace {
  ID?: string;
  Name?: string;
  Category?: string;
  Lat?: number;
  Lng?: number;
  Distance?: number | null;
  RelevanceScore?: number;
  Rating?: number | null;
  ReviewCount?: number;
}

interface RankedItem {
  Result?: RankingPlace | null;
  FinalScore?: number;
}

interface RankResponse {
  results?: RankedItem[] | null;
  algorithm?: string;
  duration_ms?: number;
}

// Reuse the existing anonymous client id (also used by liveLocationService) so
// trending/personalization key off one stable identity per browser. No new dep:
// crypto.randomUUID when available, else a timestamp+random fallback.
const SESSION_ID_KEY = 'tensormaps_user_id';

function getSessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `user-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      localStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return 'anonymous';
  }
}

// user_id keys personalization on the backend. Prefer the authenticated user id
// when signed in; otherwise fall back to the stable anonymous session id so
// anonymous visitors still get personalized ordering + trending.
function getSearchIdentity(): { userId: string; sessionId: string } {
  const sessionId = getSessionId();
  let userId = sessionId;
  try {
    const authUserId = useAuthStore.getState().user?.id;
    if (authUserId) userId = authUserId;
  } catch {
    /* store not ready — anonymous session id is fine */
  }
  return { userId, sessionId };
}

// Map a ranking-engine place (capitalized Go field names) into the frontend
// SearchResult shape. Returns null for entries missing an id/coords so callers
// never surface fabricated rows.
function normalizeRankingPlace(p: RankingPlace): SearchResult | null {
  const id = p.ID != null ? String(p.ID) : '';
  if (!id) return null;
  const name = p.Name ?? '';
  return {
    id,
    name,
    displayName: name,
    category: ((p.Category as string) || 'other') as PlaceCategory,
    coordinates: { lat: Number(p.Lat ?? 0), lng: Number(p.Lng ?? 0) },
    address: {} as Address,
    distance: typeof p.Distance === 'number' ? p.Distance : undefined,
    relevanceScore: typeof p.RelevanceScore === 'number' ? p.RelevanceScore : 1,
    rating: typeof p.Rating === 'number' ? p.Rating : undefined,
    reviewCount: typeof p.ReviewCount === 'number' ? p.ReviewCount : undefined,
  };
}

// Map a places-service /places/nearby row (GeoJSON location, category string)
// into the frontend SearchResult shape.
function normalizeNearbyPlace(p: Record<string, unknown>): SearchResult {
  const loc = p.location as { coordinates?: [number, number] } | undefined;
  const coords = loc?.coordinates;
  const lng = Array.isArray(coords) ? Number(coords[0]) : Number((p.lng as number) ?? 0);
  const lat = Array.isArray(coords) ? Number(coords[1]) : Number((p.lat as number) ?? 0);
  return {
    id: String((p.id as string | number) ?? (p.osm_id as string | number) ?? ''),
    name: (p.name as string) ?? '',
    displayName: (p.name as string) ?? (p.displayName as string) ?? '',
    category: ((p.category as string) ?? 'other') as PlaceCategory,
    coordinates: { lat, lng },
    address: (p.address as Address) ?? ({} as Address),
    distance: typeof p.distance === 'number' ? (p.distance as number) : undefined,
    relevanceScore: 1,
    osmId: p.osm_id != null ? String(p.osm_id) : undefined,
  };
}

// All methods talk to the real backend. On failure they let the error propagate
// so callers can surface a proper error/empty state — we never fabricate results.
export const searchService = {
  async search(options: SearchOptions): Promise<SearchResult[]> {
    const params = new URLSearchParams();
    params.append('q', options.query);

    if (options.location) {
      params.append('lat', options.location.lat.toString());
      params.append('lon', options.location.lng.toString());
    }

    if (options.bounds) {
      params.append('bbox', [
        options.bounds.sw.lng,
        options.bounds.sw.lat,
        options.bounds.ne.lng,
        options.bounds.ne.lat,
      ].join(','));
    }

    if (options.limit) {
      params.append('limit', options.limit.toString());
    }

    if (options.language) {
      params.append('lang', options.language);
    }

    if (options.categories?.length) {
      params.append('categories', options.categories.join(','));
    }

    const response = await api.get<SearchResult[]>(
      `/search?${params.toString()}`,
      { abortKey: SEARCH_ABORT_KEY }
    );

    return response.data;
  },

  async autocomplete(
    query: string,
    location?: Coordinates,
    limit = 5
  ): Promise<SearchResult[]> {
    if (!query || query.length < 2) {
      return [];
    }

    const params = new URLSearchParams();
    params.append('q', query);
    params.append('limit', limit.toString());

    if (location) {
      params.append('lat', location.lat.toString());
      params.append('lon', location.lng.toString());
    }

    const response = await api.get<SearchResult[]>(
      `/search/autocomplete?${params.toString()}`,
      { abortKey: AUTOCOMPLETE_ABORT_KEY }
    );

    return response.data;
  },

  async reverseGeocode(coordinates: Coordinates): Promise<ReverseGeocodeResult> {
    const params = new URLSearchParams();
    params.append('lat', coordinates.lat.toString());
    params.append('lon', coordinates.lng.toString());

    const response = await api.get<ReverseGeocodeResult>(
      `/search/reverse?${params.toString()}`
    );

    return response.data;
  },

  async getPlaceDetails(placeId: string): Promise<Place> {
    const response = await api.get<Place>(`/places/${placeId}`);
    return response.data;
  },

  async getNearbyPlaces(
    location: Coordinates,
    radius: number,
    categories?: PlaceCategory[]
  ): Promise<SearchResult[]> {
    const params = new URLSearchParams();
    params.append('lat', location.lat.toString());
    params.append('lng', location.lng.toString());
    params.append('radius', radius.toString());

    // places-service filters by a single category param
    if (categories?.length) {
      params.append('category', categories[0]);
    }

    const response = await api.get<unknown>(
      `/places/nearby?${params.toString()}`
    );

    // places-service returns { center, places: [...] } (GeoJSON locations)
    const payload = response.data as { places?: unknown[] } | unknown[];
    const list = Array.isArray(payload) ? payload : payload?.places ?? [];
    return (list as Array<Record<string, unknown>>).map(normalizeNearbyPlace);
  },

  cancelAutocomplete(): void {
    api.cancelRequest(AUTOCOMPLETE_ABORT_KEY);
  },

  // GET /ranking/trending?lat=&lng=&limit= — places trending near a location,
  // derived from real click aggregates. Returns [] on any error or when nothing
  // is trending (honest empty state — never fabricated). NOTE: trending takes
  // `lng` (not `lon` like /search).
  async getTrending(location?: Coordinates, limit = 8): Promise<SearchResult[]> {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      if (location) {
        params.append('lat', location.lat.toString());
        params.append('lng', location.lng.toString());
      }
      const response = await api.get<RankingPlace[]>(
        `/ranking/trending?${params.toString()}`
      );
      const list = Array.isArray(response.data) ? response.data : [];
      return list
        .map(normalizeRankingPlace)
        .filter((r): r is SearchResult => r !== null);
    } catch {
      return [];
    }
  },

  // POST /ranking/rank — personalized re-rank of an already-fetched candidate
  // set. Returns the candidates reordered by the engine; on ANY failure (error,
  // empty/unusable response) it returns the ORIGINAL list untouched so search
  // never visibly degrades.
  async rerankResults(
    candidates: SearchResult[],
    opts: { query: string; location?: Coordinates }
  ): Promise<SearchResult[]> {
    if (candidates.length < 2) return candidates;

    try {
      const { userId, sessionId } = getSearchIdentity();
      const body = {
        query: opts.query,
        user_id: userId,
        session_id: sessionId,
        user_lat: opts.location?.lat ?? 0,
        user_lng: opts.location?.lng ?? 0,
        // Ranking SearchResult uses capitalized (untagged) Go field names.
        results: candidates.map((c) => ({
          ID: c.id,
          Name: c.name || c.displayName,
          Category: c.category,
          Lat: c.coordinates.lat,
          Lng: c.coordinates.lng,
          RelevanceScore: c.relevanceScore ?? 0,
          ...(typeof c.distance === 'number' ? { Distance: c.distance } : {}),
        })),
      };

      const response = await api.post<RankResponse>('/ranking/rank', body);
      const ranked = response.data?.results;
      if (!Array.isArray(ranked) || ranked.length === 0) return candidates;

      // Reorder the original candidates by the returned ID order, preserving all
      // frontend fields. Any candidate the engine dropped is appended so nothing
      // silently disappears.
      const byId = new Map(candidates.map((c) => [c.id, c]));
      const ordered: SearchResult[] = [];
      const seen = new Set<string>();
      for (const item of ranked) {
        const id = item.Result?.ID != null ? String(item.Result.ID) : '';
        const match = id ? byId.get(id) : undefined;
        if (match && !seen.has(id)) {
          ordered.push(match);
          seen.add(id);
        }
      }
      for (const c of candidates) {
        if (!seen.has(c.id)) ordered.push(c);
      }
      return ordered.length ? ordered : candidates;
    } catch {
      return candidates;
    }
  },

  // POST /ranking/click — fire-and-forget signal that a result was chosen. Best
  // effort: never awaited by callers, never throws.
  recordClick(input: {
    result: SearchResult;
    query?: string;
    position?: number;
  }): void {
    try {
      const { userId } = getSearchIdentity();
      const { result } = input;
      void api
        .post('/ranking/click', {
          user_id: userId,
          result_id: result.id,
          category: result.category,
          query: input.query ?? '',
          position: input.position ?? 0,
          // Optional metadata lets the backend render this place under trending.
          name: result.name || result.displayName,
          lat: result.coordinates.lat,
          lng: result.coordinates.lng,
        })
        .catch(() => {});
    } catch {
      /* never let a feedback signal break the UI */
    }
  },

  // POST /ranking/dwell — fire-and-forget engagement signal for a place opened
  // from search. Best effort: never awaited, never throws.
  recordDwell(input: {
    result: SearchResult;
    query?: string;
    dwellTimeMs?: number;
  }): void {
    try {
      const { userId } = getSearchIdentity();
      void api
        .post('/ranking/dwell', {
          user_id: userId,
          result_id: input.result.id,
          query: input.query ?? '',
          dwell_time_ms: input.dwellTimeMs ?? 0,
        })
        .catch(() => {});
    } catch {
      /* best effort */
    }
  },
};
